import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useLocation, useParams } from "react-router";

import { DataTable } from "../../components/DataTable";
import { Alert, Badge, Card, LoadingBlock, PageHeader, TextField } from "../../components/ui";
import { api, errorMessage } from "../../lib/api";
import { useConfig } from "../../lib/config";
import { formatBytes, formatDate, formatDateTime } from "../../lib/format";
import type { Option, Page } from "../../lib/types";
import { DueText } from "./OrderBits";
import { CustomerOrderActions } from "./OrderActions";
import type { OrderDetail, OrderSummary } from "./types";

const linkBtn =
  "inline-flex min-h-11 items-center rounded-md bg-brand-600 px-4 font-semibold text-white hover:bg-brand-700";

export function OrdersListPage() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" }>({
    key: "submitted",
    dir: "desc",
  });
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const query = useQuery({
    queryKey: ["orders", page, q, sort],
    queryFn: () => {
      const p = new URLSearchParams({
        page: String(page),
        page_size: "20",
        sort: sort.key,
        direction: sort.dir,
      });
      if (q) p.set("q", q);
      return api.get<Page<OrderSummary>>(`/orders?${p}`);
    },
  });
  const drafts = useQuery({
    queryKey: ["drafts"],
    queryFn: () =>
      api.get<{ id: string; data: { project_name_number?: string }; updated_at: string }[]>(
        "/orders/drafts",
      ),
  });

  return (
    <div>
      <PageHeader
        title="Orders TO Bill's"
        description="Print orders you and your company have sent to Bill's."
        actions={
          <Link to="/orders/new" className={linkBtn}>
            New order
          </Link>
        }
      />
      {drafts.data && drafts.data.length > 0 && (
        <Card className="mb-4">
          <h2 className="font-bold">Saved drafts</h2>
          <ul className="mt-2 flex flex-col gap-1">
            {drafts.data.map((d) => (
              <li key={d.id}>
                <Link className="text-brand-700 underline" to={`/orders/new?draft=${d.id}`}>
                  {d.data.project_name_number || "Untitled draft"}
                </Link>{" "}
                <span className="text-sm text-ink-muted">
                  saved {formatDateTime(d.updated_at, tz)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <TextField
          label="Search"
          placeholder="Order number or project"
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
        />
      </div>
      <DataTable
        caption="Your orders"
        loading={query.isLoading}
        error={query.error ? errorMessage(query.error) : null}
        rows={query.data?.items}
        rowKey={(o) => o.id}
        emptyTitle="No orders yet"
        emptyBody={
          <Link to="/orders/new" className="text-brand-700 underline">
            Send your first order to Bill's
          </Link>
        }
        sort={sort}
        onSort={(key) =>
          setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }))
        }
        page={page}
        pageSize={20}
        total={query.data?.total}
        onPage={setPage}
        columns={[
          {
            key: "number",
            header: "Order",
            sortable: true,
            render: (o) => (
              <Link className="font-semibold text-brand-700 underline" to={`/orders/${o.id}`}>
                {o.order_number}
              </Link>
            ),
          },
          { key: "project", header: "Project", render: (o) => o.project_name_number },
          { key: "for", header: "For", render: (o) => o.company_name ?? "Me" },
          { key: "due", header: "Due", sortable: true, render: (o) => <DueText order={o} /> },
          {
            key: "submitted",
            header: "Submitted",
            sortable: true,
            render: (o) => formatDate(o.submitted_at, tz),
          },
        ]}
      />
    </div>
  );
}

export function OrderSubmittedPage() {
  const { id } = useParams();
  const location = useLocation();
  const number = (location.state as { orderNumber?: string } | null)?.orderNumber;
  return (
    <div className="mx-auto max-w-xl">
      <Card>
        <h1 className="text-2xl font-bold text-ok">Thank you. Your order was submitted.</h1>
        {number && (
          <p className="mt-4 text-lg">
            Your order number is <strong className="font-mono text-2xl">{number}</strong>
          </p>
        )}
        <p className="mt-3 text-ink-muted">
          We sent a confirmation email. Bill's will confirm your requested due date and let you know
          when your order is ready.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to={`/orders/${id}`} className={linkBtn}>
            View order
          </Link>
          <Link
            to="/orders/new"
            className="inline-flex min-h-11 items-center px-3 text-brand-700 underline"
          >
            Start another order
          </Link>
        </div>
      </Card>
    </div>
  );
}

export function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-sm font-semibold text-ink-muted">{label}</dt>
      <dd className="break-words">{children || "—"}</dd>
    </div>
  );
}

export function OrderInfo({ order, staff }: { order: OrderDetail; staff?: boolean }) {
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const o = config?.options;
  const lbl = (opts: Option[] | undefined, v: string) =>
    opts?.find((x) => x.value === v)?.label ?? v;
  const downloadBase = staff ? `/api/v1/staff/orders/${order.id}` : `/api/v1/orders/${order.id}`;
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <h2 className="mb-3 text-lg font-bold">Order details</h2>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
          <Detail label="Project">{order.project_name_number}</Detail>
          <Detail label="Ordered for">{order.company_name ?? "Individual"}</Detail>
          <Detail label="Submitted by">{order.submitted_by_name}</Detail>
          <Detail label="Department">{lbl(o?.departments, order.department)}</Detail>
          <Detail label="Originals">{order.originals_count}</Detail>
          <Detail label="Printed sets">{order.printed_sets}</Detail>
          <Detail label="Size">{lbl(o?.print_sizes, order.print_size)}</Detail>
          <Detail label="Binding">
            {order.binding === "other"
              ? `Other: ${order.binding_other}`
              : lbl(o?.bindings, order.binding)}
          </Detail>
          <Detail label="Payment">{lbl(o?.payment_methods, order.payment_method)}</Detail>
          <div className="sm:col-span-3">
            <Detail label="Notes">
              <span className="whitespace-pre-wrap">{order.notes}</span>
            </Detail>
          </div>
        </dl>
      </Card>
      <Card>
        <h2 className="mb-3 text-lg font-bold">Contact and delivery</h2>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
          <Detail label="Customer">{order.customer_name}</Detail>
          <Detail label="Phone">{order.customer_phone}</Detail>
          <Detail label="Email">{order.customer_email}</Detail>
          <Detail label="Address">
            {order.customer_street}, {order.customer_city}, {order.customer_state}{" "}
            {order.customer_zip}
          </Detail>
          <Detail label="Delivery">{lbl(o?.delivery_methods, order.delivery_method)}</Detail>
          {order.ship_street && (
            <Detail label="Deliver to">
              {order.ship_recipient}, {order.ship_street}, {order.ship_city}, {order.ship_state}{" "}
              {order.ship_zip}
            </Detail>
          )}
          {order.ship_instructions && (
            <Detail label="Instructions">{order.ship_instructions}</Detail>
          )}
          {order.tracking_number && (
            <Detail label="Tracking">
              {order.tracking_carrier} {order.tracking_number}
            </Detail>
          )}
          {order.shipping_notes && <Detail label="Shipping notes">{order.shipping_notes}</Detail>}
        </dl>
      </Card>
      <Card>
        <h2 className="mb-3 text-lg font-bold">Files ({order.files.length})</h2>
        {order.file_expires_at && (
          <p className="mb-2 text-sm text-ink-muted">
            These files will be removed on {formatDate(order.file_expires_at, tz)}.
          </p>
        )}
        <ul className="divide-y divide-line">
          {order.files.map((f) => (
            <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span>
                {f.name}{" "}
                <span className="text-sm text-ink-muted">({formatBytes(f.size_bytes)})</span>
              </span>
              {f.available ? (
                <a
                  className="text-brand-700 underline"
                  href={`${downloadBase}/files/${f.id}/download`}
                >
                  Download
                </a>
              ) : (
                <Badge>Removed</Badge>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

export function OrderDetailPage() {
  const { id } = useParams();
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const query = useQuery({
    queryKey: ["order", id],
    queryFn: () => api.get<OrderDetail>(`/orders/${id}`),
  });
  if (query.isLoading) return <LoadingBlock />;
  if (query.error || !query.data) return <Alert tone="error">{errorMessage(query.error)}</Alert>;
  const order = query.data;
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={`Order ${order.order_number}`}
        description={<span>Submitted {formatDateTime(order.submitted_at, tz)}</span>}
        actions={
          <Link
            to={`/orders/new?duplicate=${order.id}`}
            className="inline-flex min-h-11 items-center rounded-md border border-brand-600 bg-white px-4 font-semibold text-brand-700 hover:bg-brand-50"
          >
            Duplicate order
          </Link>
        }
      />
      <Card>
        <h2 className="text-lg font-bold">Due date</h2>
        <p className="mt-2">{formatDateTime(order.effective_due_at, tz)}</p>
      </Card>
      <CustomerOrderActions order={order} />
      <OrderInfo order={order} />
    </div>
  );
}
